/**
 * Custom SVG icons for Play Mode navigation.
 * Matches the flat aesthetic of the dice icons.
 */

interface IconProps {
  className?: string;
}

/** Sheet icon - Scroll/parchment design */
export function SheetIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Scroll outline */}
      <path d="M6 4h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      {/* Text lines */}
      <path d="M8 9h8" strokeOpacity="0.7" />
      <path d="M8 13h8" strokeOpacity="0.7" />
      <path d="M8 17h5" strokeOpacity="0.7" />
    </svg>
  );
}

/** Combat icon - Crossed swords */
export function CombatIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Sword 1 */}
      <path d="M5 5l6 6m-4 4l-2 2-1-1 2-2" />
      <path d="M11 11l-1.5 1.5" strokeOpacity="0.5" />

      {/* Sword 2 */}
      <path d="M19 5l-6 6m4 4l2 2 1-1-2-2" />
      <path d="M13 11l1.5 1.5" strokeOpacity="0.5" />

      {/* Hilts */}
      <circle cx="5" cy="19" r="1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="19" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Role Play icon - Theater masks (comedy/tragedy) */
export function RolePlayIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Left mask (happy) */}
      <path d="M9 3a6 6 0 0 1 0 12 6 6 0 0 1 0-12z" />
      <circle cx="7" cy="7" r="0.8" fill="currentColor" stroke="none" />
      <circle cx="11" cy="7" r="0.8" fill="currentColor" stroke="none" />
      <path d="M7 11a2 2 0 0 0 4 0" strokeOpacity="0.7" />

      {/* Right mask (sad) */}
      <path d="M15 9a6 6 0 0 1 0 12 6 6 0 0 1 0-12z" />
      <circle cx="13" cy="13" r="0.8" fill="currentColor" stroke="none" />
      <circle cx="17" cy="13" r="0.8" fill="currentColor" stroke="none" />
      <path d="M17 17a2 2 0 0 1-4 0" strokeOpacity="0.7" />
    </svg>
  );
}

/** Floating dice button icon - Isometric D6 matching dice roller aesthetic */
export function FloatingDiceIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Isometric cube - 3 visible faces */}
      {/* Top face */}
      <path d="M12 2L22 7L12 12L2 7L12 2z" />
      {/* Left face */}
      <path d="M2 7v10l10 5V12L2 7z" />
      {/* Right face */}
      <path d="M22 7v10l-10 5V12l10-5z" />
      {/* Pip on top face */}
      <circle cx="12" cy="7" r="1.2" fill="currentColor" stroke="none" />
      {/* Pips on left face (2 pips diagonal) */}
      <circle cx="5" cy="10" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
      <circle cx="9" cy="16" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
      {/* Pips on right face (3 pips diagonal) */}
      <circle cx="19" cy="10" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
      <circle cx="17" cy="13" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
      <circle cx="15" cy="16" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
    </svg>
  );
}

/** Bond icon - Chain link representing connection */
export function BondIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Left chain link */}
      <path d="M9 12a3 3 0 0 1 3-3h1a3 3 0 0 1 0 6h-1a3 3 0 0 1-3-3z" />
      {/* Right chain link */}
      <path d="M11 12a3 3 0 0 1 3-3h1a3 3 0 0 1 0 6h-1a3 3 0 0 1-3-3z" />
      {/* Connection line */}
      <path d="M8 12h8" strokeOpacity="0.5" />
    </svg>
  );
}

/** Ideal icon - Balance scale representing values/morality */
export function IdealIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Central pillar */}
      <path d="M12 3v18" />
      {/* Balance beam */}
      <path d="M4 7h16" />
      {/* Left scale pan */}
      <path d="M4 7v2a3 3 0 0 0 6 0V7" />
      {/* Right scale pan */}
      <path d="M14 7v2a3 3 0 0 0 6 0V7" />
      {/* Base */}
      <path d="M8 21h8" />
    </svg>
  );
}

/** Bardic Inspiration icon - Music note */
export function BardicInspirationIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Note stem */}
      <path d="M9 4v12" />
      {/* Note head */}
      <ellipse cx="6" cy="16" rx="3" ry="2" fill="currentColor" stroke="none" />
      {/* Flag */}
      <path d="M9 4c4 0 6 2 6 5" />
    </svg>
  );
}

/** DM Inspiration icon - Star/diamond */
export function DmInspirationIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* 4-pointed star */}
      <path d="M12 2l2.5 7.5H22l-6 4.5 2.5 7.5L12 17l-6.5 4.5L8 14 2 9.5h7.5L12 2z" />
    </svg>
  );
}

/** Custom Effect icon - Sparkle */
export function CustomEffectIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Main sparkle */}
      <path d="M12 3v4m0 10v4M3 12h4m10 0h4" />
      {/* Diagonal sparkle */}
      <path d="M5.6 5.6l2.8 2.8m7.2 7.2l2.8 2.8M18.4 5.6l-2.8 2.8M5.6 18.4l2.8-2.8" strokeOpacity="0.6" />
      {/* Center dot */}
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Flaw icon - Cracked heart representing weakness/vulnerability */
export function FlawIcon({ className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Heart outline */}
      <path d="M12 6C10 3 6 3 4 6s-2 7 8 13c10-6 10-10 8-13s-6-3-8 0z" />
      {/* Crack through the heart */}
      <path d="M12 8l-1 3 2 2-1 3" strokeOpacity="0.7" />
    </svg>
  );
}
